import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import {
  X,
  Mail,
  Phone,
  MapPin,
  Building2,
  CalendarDays,
  Plus,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  Clock,
  MessageCircle,
  RefreshCw,
  Check,
  Edit3,
  FileText,
  CheckCircle2,
  Loader2,
  Link2,
  LockKeyhole,
  Info,
  Save,
  ChevronRight,
} from 'lucide-react';
import { useCRM } from '../lib/context';
import { api, money, dateLabel } from '../lib/api';
import { contactBudgetLabel } from '../lib/contact-budget';
import type { Entity } from '../lib/types';
import { Avatar, Badge, Tabs, Score, SectionHeading, EmptyState } from './ui';
import { PropertyImage } from '../pages/Core';
import { ContactEvidence } from './ContactEvidence';
import { useDialogFocus } from '../lib/dialog';
import { canWriteEntity, canGovernContact, canManageWorkspace } from '../lib/permissions';
function contactDraft(c: Entity) {
  return {
    version: c.version,
    name: c.name,
    company: c.company,
    email: c.email,
    phone: c.phone,
    budgetMin: c.budgetMin,
    budgetMax: c.budgetMax,
    localities: c.localities?.join(', '),
    configuration: c.configuration,
    purpose: c.purpose,
    timeline: c.timeline,
    notes: c.notes,
    preferredChannel: c.preferredChannel || '',
    language: c.language || '',
    contactTime: c.contactTime || '',
    sizeMin: c.sizeMin || '',
    sizeMax: c.sizeMax || '',
    propertyType: c.propertyType || 'Any',
    possessionBy: c.possessionBy || '',
    financing: c.financing || '',
    amenities: (c.amenities || []).join(', '),
    acceptableLocalities: (c.acceptableLocalities || []).join(', '),
  };
}
export function ContactDrawer({ contact: c, onClose }: { contact: Entity; onClose: () => void }) {
  const { data, mutate, notify, openCreate, navigate } = useCRM();
  const canEdit = canWriteEntity(data.user, 'contacts', c);
  const canGovern = canGovernContact(data.user.role);
  const canSuppress = canManageWorkspace(data.user.role);
  const [tab, setTab] = useState('Overview');
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('Note');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<any>(() => contactDraft(c));
  function startEditing() {
    setForm(contactDraft(c));
    setEdit(true);
    setTab('Overview');
  }
  const [matches, setMatches] = useState<any[]>([]);
  const [brief, setBrief] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [mergeId, setMergeId] = useState('');
  const [mergeReason, setMergeReason] = useState('');
  const [lastMerge, setLastMerge] = useState('');
  const [permission, setPermission] = useState({
    channel: 'email',
    allowed: false,
    purpose: 'direct_sales',
    source: '',
    evidence: '',
    reason: '',
  });
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideValue, setOverrideValue] = useState(String(c.score));
  const [overrideReason, setOverrideReason] = useState('');
  const [matchError, setMatchError] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);
  const interactions = data.interactions
    .filter((i) => i.contactId === c.id)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const opportunities = data.opportunities.filter((o) => o.contactId === c.id);
  const tasks = data.tasks.filter((t) => t.contactId === c.id && !t.completed);
  useEffect(() => {
    let alive = true;
    setMatchError('');
    api(`/intelligence/matches/${c.id}`)
      .then((r) => {
        if (alive) setMatches(r.matches || r.data || r);
      })
      .catch((e) => {
        if (alive) setMatchError(e.message);
      });
    return () => {
      alive = false;
    };
  }, [c.id, c.version]);
  useDialogFocus(drawerRef, onClose);
  async function addNote() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await mutate('/interactions', 'POST', {
        contactId: c.id,
        type: noteType,
        title: noteType === 'Call' ? 'Call outcome recorded' : 'Advisor note',
        body: note,
        actor: data.user.name,
      });
      setNote('');
      notify('Added to the relationship timeline');
    } catch {
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    setBusy(true);
    try {
      await mutate(`/contacts/${c.id}`, 'PATCH', {
        ...form,
        budgetMin: Number(form.budgetMin),
        budgetMax: Number(form.budgetMax),
        sizeMin: Number(form.sizeMin) || 0,
        sizeMax: Number(form.sizeMax) || 0,
        localities: form.localities
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        amenities: form.amenities
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        acceptableLocalities: form.acceptableLocalities
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        version: form.version,
      });
      setEdit(false);
      notify('Relationship details updated');
    } catch {
    } finally {
      setBusy(false);
    }
  }
  async function refreshScore() {
    setBusy(true);
    try {
      await mutate(`/intelligence/score/${c.id}`, 'POST', {});
      notify('Buyer score refreshed from current evidence');
    } catch {
    } finally {
      setBusy(false);
    }
  }
  async function getBrief() {
    setBusy(true);
    try {
      setBrief(
        await api('/copilot/generate', 'POST', {
          contactId: c.id,
          query: 'Prepare an advisor brief',
        }),
      );
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }
  async function suppression() {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      await mutate(`/contacts/${c.id}/suppress`, 'POST', {
        suppressed: !c.suppressed,
        reason,
        version: c.version,
      });
      setReason('');
      notify(
        c.suppressed
          ? 'Global suppression lifted; channel permissions still apply'
          : 'Contact suppressed across all outreach channels',
      );
    } catch {
    } finally {
      setBusy(false);
    }
  }
  return (
    <motion.div
      className="drawer-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.aside
        ref={drawerRef}
        tabIndex={-1}
        className="contact-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${c.name} contact details`}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 34 }}
      >
        <div className="drawer-top">
          <span>
            <UsersIcon />
            RELATIONSHIP 360
          </span>
          <div>
            <button
              className="icon-btn"
              aria-label="Edit relationship"
              disabled={!canEdit}
              onClick={() => {
                if (edit) setEdit(false);
                else startEditing();
              }}
            >
              <Edit3 size={17} />
            </button>
            <button className="icon-btn" aria-label="Close relationship details" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="contact-profile">
          <Avatar name={c.name} size={68} />
          <div>
            <h2>{c.name}</h2>
            <p>
              {c.role}
              {c.company && ` at ${c.company}`}
            </p>
            <span>
              <MapPin size={12} />
              {c.localities?.join(' · ') || 'Locality not recorded'}
              <i />
              <Badge tone="green">{c.stage}</Badge>
            </span>
          </div>
        </div>
        <div className="contact-quick-actions">
          <button
            className="btn"
            onClick={() => {
              setTab('Timeline');
              setNoteType('Call');
            }}
          >
            <Phone size={14} />
            Log call
          </button>
          <button
            className="btn"
            onClick={() => {
              onClose();
              navigate('communications');
            }}
          >
            <Mail size={14} />
            Compose
          </button>
          <button
            className="btn"
            onClick={() => {
              onClose();
              openCreate('visits', { contactId: c.id });
            }}
          >
            <CalendarDays size={14} />
            Plan a visit
          </button>
          <button className="btn btn-soft" onClick={getBrief} disabled={busy || !canEdit}>
            <Sparkles size={14} />
            Advisor brief
          </button>
        </div>
        <div className="drawer-tab-wrap">
          <Tabs
            items={['Overview', 'Timeline', 'Matches', 'Permissions', 'Evidence']}
            value={tab}
            onChange={setTab}
          />
        </div>
        <div className="drawer-content">
          {tab === 'Evidence' && <ContactEvidence contactId={c.id} version={c.version} />}
          {brief && (
            <div className="advisor-brief">
              <div>
                <Sparkles size={15} />
                <strong>Your evidence brief</strong>
                <button
                  className="icon-btn"
                  aria-label="Close brief"
                  onClick={() => setBrief(null)}
                >
                  <X size={14} />
                </button>
              </div>
              <p>{brief.answer}</p>
              {brief.suggestions?.length > 0 && (
                <ul>
                  {brief.suggestions.map((suggestion: string) => (
                    <li key={suggestion}>{suggestion}</li>
                  ))}
                </ul>
              )}
              <div className="brief-evidence">
                {brief.evidence?.map((e: any, i: number) => (
                  <span key={i}>
                    <Link2 size={10} />
                    {e.label}
                  </span>
                ))}
              </div>
              <small>{brief.disclaimer}</small>
              {brief.outputId && (
                <div className="brief-feedback">
                  <small>{brief.model} · Draft for review</small>
                  {brief.feedback ? (
                    <Badge tone="green">Feedback recorded</Badge>
                  ) : (
                    <>
                      {['accepted', 'rejected'].map((feedback) => (
                        <button
                          key={feedback}
                          className="btn btn-sm btn-ghost"
                          onClick={async () => {
                            try {
                              await api('/copilot/feedback', 'POST', {
                                outputId: brief.outputId,
                                feedback,
                              });
                              setBrief({ ...brief, feedback });
                            } catch (error) {
                              notify((error as Error).message, 'error');
                            }
                          }}
                        >
                          {feedback === 'accepted' ? 'Useful' : 'Needs correction'}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {tab === 'Overview' && (
            <>
              {edit ? (
                <section>
                  <SectionHeading title="The details that matter" />
                  <div className="form-grid">
                    {[
                      { name: 'name', label: 'Full name' },
                      { name: 'company', label: 'Company' },
                      { name: 'email', label: 'Email', type: 'email' },
                      { name: 'phone', label: 'Phone' },
                      { name: 'preferredChannel', label: 'Preferred channel' },
                      { name: 'language', label: 'Preferred language' },
                      { name: 'contactTime', label: 'Best time to contact' },
                      { name: 'budgetMin', label: 'Budget from (₹)', type: 'number' },
                      { name: 'budgetMax', label: 'Budget to (₹)', type: 'number' },
                      { name: 'localities', label: 'Localities, comma separated' },
                      { name: 'configuration', label: 'Configuration' },
                      { name: 'purpose', label: 'Purpose' },
                      { name: 'timeline', label: 'Purchase timeline' },
                      { name: 'sizeMin', label: 'Minimum area (sq. ft.)', type: 'number' },
                      { name: 'sizeMax', label: 'Maximum area (sq. ft.)', type: 'number' },
                      { name: 'propertyType', label: 'Property type (Any, Apartment, Villa)' },
                      { name: 'possessionBy', label: 'Possession deadline', type: 'date' },
                      { name: 'financing', label: 'Financing status' },
                      { name: 'amenities', label: 'Preferred amenities, comma separated' },
                      { name: 'acceptableLocalities', label: 'Accepted nearby localities' },
                    ].map((f) => (
                      <label className="field" key={f.name}>
                        <span>{f.label}</span>
                        <input
                          type={f.type || 'text'}
                          value={form[f.name] || ''}
                          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        />
                      </label>
                    ))}
                    <label className="field field-full">
                      <span>Notes</span>
                      <textarea
                        value={form.notes}
                        rows={3}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      />
                    </label>
                  </div>
                  <div className="modal-footer">
                    <button className="btn" onClick={() => setEdit(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" disabled={busy || !canEdit} onClick={save}>
                      <Save size={14} />
                      Save relationship
                    </button>
                  </div>
                </section>
              ) : (
                <>
                  <section className="buyer-score-panel">
                    <div className="buyer-score-top">
                      <div>
                        <span className="eyebrow">BUYER OPPORTUNITY SCORE</span>
                        <div className="buyer-score-number">
                          {Math.round(c.score)}
                          <span>/ 100</span>
                          <Badge tone={c.score >= 85 ? 'green' : 'amber'}>
                            {c.suppressed ? 'Suppressed' : c.scoreBand || 'Developing'}
                          </Badge>
                        </div>
                      </div>
                      <button
                        className="icon-btn"
                        disabled={busy || !canEdit}
                        aria-label="Recalculate buyer score"
                        onClick={refreshScore}
                      >
                        <RefreshCw size={17} className={busy ? 'spin' : ''} />
                      </button>
                    </div>
                    <div className="score-components">
                      {Object.entries(c.scoreComponents || {}).map(([name, value]) => (
                        <div key={name}>
                          <span>
                            {name === 'fit'
                              ? 'Property fit'
                              : name === 'capacity'
                                ? 'Capacity band'
                                : name}
                          </span>
                          <div>
                            <i style={{ width: `${value}%` }} />
                          </div>
                          <b>{Math.round(value as number)}</b>
                        </div>
                      ))}
                    </div>
                    <div className="score-evidence">
                      <ShieldCheck size={12} />
                      {Math.round(c.confidence * 100)}% data confidence<span>{c.scoreVersion}</span>
                    </div>
                    <div className="score-override-toggle">
                      <span>
                        Model score: {Math.round(c.modelScore ?? c.score)}
                        {c.scoreOverride
                          ? ` · Advisor override until ${dateLabel(c.scoreOverride.expiresAt)}`
                          : ''}
                      </span>
                      <button
                        className="text-link"
                        disabled={!canEdit}
                        onClick={() => setOverrideOpen(!overrideOpen)}
                      >
                        Advisor review
                      </button>
                    </div>
                    {overrideOpen && (
                      <div className="override-form">
                        <label className="field">
                          <span>Reviewed score (0–100)</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={overrideValue}
                            onChange={(e) => setOverrideValue(e.target.value)}
                          />
                        </label>
                        <label className="field">
                          <span>Supporting reason</span>
                          <input
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="Record the buyer-confirmed context"
                          />
                        </label>
                        <div>
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={
                              overrideReason.trim().length < 5 ||
                              Number(overrideValue) < 0 ||
                              Number(overrideValue) > 100
                            }
                            onClick={async () => {
                              try {
                                await mutate(`/contacts/${c.id}/score-override`, 'POST', {
                                  version: c.version,
                                  value: Number(overrideValue),
                                  reason: overrideReason,
                                });
                                setOverrideOpen(false);
                                notify('Advisor review recorded; model score preserved');
                              } catch {}
                            }}
                          >
                            Save 7-day override
                          </button>
                          {c.scoreOverride && (
                            <button
                              className="btn btn-sm"
                              disabled={overrideReason.trim().length < 5}
                              onClick={async () => {
                                try {
                                  await mutate(`/contacts/${c.id}/score-override`, 'POST', {
                                    version: c.version,
                                    value: null,
                                    reason: overrideReason,
                                  });
                                  setOverrideOpen(false);
                                  notify('Model score restored');
                                } catch {}
                              }}
                            >
                              Clear override
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </section>
                  <section className="drawer-section">
                    <SectionHeading
                      title="A picture of their next home"
                      action={
                        <button className="text-link" disabled={!canEdit} onClick={startEditing}>
                          Refine <Edit3 size={12} />
                        </button>
                      }
                    />
                    <div className="requirement-grid">
                      <div>
                        <small>STATED BUDGET</small>
                        <strong>{contactBudgetLabel(c)}</strong>
                      </div>
                      <div>
                        <small>CONFIGURATION</small>
                        <strong>{c.configuration || 'To be confirmed'}</strong>
                      </div>
                      <div>
                        <small>PREFERRED LOCALITIES</small>
                        <strong>{c.localities?.join(', ') || 'To be confirmed'}</strong>
                      </div>
                      <div>
                        <small>PURCHASE HORIZON</small>
                        <strong>{c.timeline || 'To be confirmed'}</strong>
                      </div>
                      <div>
                        <small>PURPOSE</small>
                        <strong>{c.purpose || 'To be confirmed'}</strong>
                      </div>
                      <div>
                        <small>RELATIONSHIP ADVISOR</small>
                        <strong>{c.owner}</strong>
                      </div>
                    </div>
                    {c.notes && (
                      <div className="contact-notes">
                        <FileText size={16} />
                        <p>{c.notes}</p>
                      </div>
                    )}
                  </section>
                  <section className="drawer-section">
                    <SectionHeading title="Ways to connect" />
                    <div className="contact-info-row">
                      <Mail size={16} />
                      <span>{c.email || 'Email not yet recorded'}</span>
                      {c.emailVerified && <ShieldCheck size={14} />}
                    </div>
                    <div className="contact-info-row">
                      <Phone size={16} />
                      <span>{c.phone || 'Phone not yet recorded'}</span>
                      {c.phoneVerified && <ShieldCheck size={14} />}
                    </div>
                    <div className="contact-tags">
                      {c.tags?.map((tag: string) => (
                        <Badge key={tag}>{tag}</Badge>
                      ))}
                    </div>
                  </section>
                  <section className="drawer-section">
                    <SectionHeading
                      title="Opportunities in motion"
                      action={
                        <button
                          className="text-link"
                          onClick={() => {
                            onClose();
                            openCreate('opportunities', { contactId: c.id });
                          }}
                        >
                          <Plus size={13} />
                          New
                        </button>
                      }
                    />
                    {opportunities.map((o) => (
                      <div className="contact-opportunity" key={o.id}>
                        <div>
                          <strong>{o.title}</strong>
                          <span>{o.nextAction}</span>
                        </div>
                        <div>
                          <strong>{money(o.value)}</strong>
                          <Badge>{o.stage}</Badge>
                        </div>
                      </div>
                    ))}
                    {!opportunities.length && (
                      <p className="muted">
                        No opportunity yet. A conversation is a good place to start.
                      </p>
                    )}
                  </section>
                </>
              )}
            </>
          )}
          {tab === 'Timeline' && (
            <>
              <div className="note-composer">
                <div>
                  <h3>Keep the context alive</h3>
                  <select
                    aria-label="Interaction type"
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                  >
                    {['Note', 'Call', 'Meeting', 'Email', 'WhatsApp'].map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  placeholder={
                    noteType === 'Call'
                      ? 'Record disposition, requirements and the agreed next step...'
                      : 'What should the next conversation build on?'
                  }
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  className="btn btn-primary btn-sm"
                  disabled={busy || !canEdit || !note.trim()}
                  onClick={addNote}
                >
                  <Plus size={14} />
                  Add to timeline
                </button>
              </div>
              <div className="contact-timeline">
                {interactions.map((i) => (
                  <div className="timeline-item" key={i.id}>
                    <span className="timeline-icon">
                      {i.type === 'Call' ? (
                        <Phone size={14} />
                      ) : i.type === 'System' ? (
                        <ShieldCheck size={14} />
                      ) : (
                        <MessageCircle size={14} />
                      )}
                    </span>
                    <div>
                      <span className="timeline-date">
                        {dateLabel(i.createdAt)} ·{' '}
                        {new Date(i.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Asia/Kolkata',
                        })}
                      </span>
                      <h4>{i.title}</h4>
                      <p>{i.body}</p>
                      <small>{i.actor}</small>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {tab === 'Matches' && (
            <>
              <SectionHeading
                title="A place that feels like them"
                subtitle={`${matches.filter((m) => m.eligible !== false).length} eligible properties · pinned exceptions remain visible for review.`}
              />
              {matches.map((m) => (
                <div className="drawer-match" key={m.propertyId}>
                  <div className="drawer-match-image">
                    <PropertyImage property={m.property} />
                  </div>
                  <div>
                    <span className="eyebrow">{m.property.locality}</span>
                    <h3>{m.property.name}</h3>
                    <p>
                      {m.property.bhk} ·{' '}
                      {m.property.catalogOnly && !(Number(m.property.price) > 0)
                        ? m.property.sourcePriceText || 'Price not specified'
                        : money(m.property.price)}
                    </p>
                    {m.property.catalogOnly && (
                      <p className="match-warning">Catalog listing · needs review</p>
                    )}
                    <div className="drawer-match-reasons">
                      {m.reasons?.slice(0, 2).map((r: string) => (
                        <span key={r}>
                          <Check size={11} />
                          {r.replaceAll('_', ' ')}
                        </span>
                      ))}
                    </div>
                    {m.warnings?.length > 0 && (
                      <p className="match-warning">{m.warnings.join(' ')}</p>
                    )}
                  </div>
                  {m.eligible === false ? (
                    <Badge tone="amber">Recheck</Badge>
                  ) : (
                    <Score value={m.score} />
                  )}
                </div>
              ))}
              {matchError && <p className="form-error">{matchError}</p>}
              {!matches.length && !matchError && (
                <EmptyState
                  icon={Building2}
                  title="The right space is still out there"
                  description="No available properties meet the hard constraints. Refine the requirement or add verified inventory."
                />
              )}
            </>
          )}
          {tab === 'Permissions' && (
            <>
              <div className="permission-intro">
                <ShieldCheck size={25} />
                <h3>A relationship built on trust.</h3>
                <p>
                  Permissions and provenance travel with this record. Suppression always takes
                  precedence over a buyer score.
                </p>
              </div>
              <div className="permission-channels">
                {Object.entries(c.consent || {}).map(([channel, allowed]) => (
                  <div key={channel}>
                    <span className="channel-icon">
                      {channel === 'email' ? (
                        <Mail size={18} />
                      ) : channel === 'phone' ? (
                        <Phone size={18} />
                      ) : channel === 'whatsapp' ? (
                        <MessageCircle size={18} />
                      ) : (
                        <TargetIcon />
                      )}
                    </span>
                    <span>
                      <strong>{channel === 'ads' ? 'Advertising permissions' : channel}</strong>
                      <small>
                        {c.suppressed
                          ? 'Blocked by global suppression'
                          : allowed
                            ? 'Permission recorded'
                            : 'Permission not confirmed'}
                      </small>
                    </span>
                    <Badge tone={allowed && !c.suppressed ? 'green' : 'gray'}>
                      {allowed && !c.suppressed ? 'Allowed' : 'Blocked'}
                    </Badge>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-soft permission-edit"
                onClick={() => setPermissionOpen(!permissionOpen)}
                disabled={!canGovern}
              >
                <ShieldCheck size={14} />
                Record channel permission
              </button>
              {permissionOpen && (
                <form
                  className="permission-editor"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBusy(true);
                    try {
                      await mutate(`/contacts/${c.id}/consent`, 'POST', {
                        version: c.version,
                        channel: permission.channel,
                        allowed: permission.allowed,
                        purposes: permission.purpose.split('+'),
                        source: permission.source,
                        evidence: permission.evidence,
                        reason: permission.reason,
                      });
                      setPermissionOpen(false);
                      notify('Permission and evidence recorded');
                    } catch {
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <div className="form-grid">
                    <label className="field">
                      <span>Channel</span>
                      <select
                        value={permission.channel}
                        onChange={(e) =>
                          setPermission({
                            ...permission,
                            channel: e.target.value,
                            purpose: e.target.value === 'ads' ? 'advertising' : 'direct_sales',
                          })
                        }
                      >
                        {['email', 'phone', 'whatsapp', 'ads'].map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Permission decision</span>
                      <select
                        value={String(permission.allowed)}
                        onChange={(e) =>
                          setPermission({ ...permission, allowed: e.target.value === 'true' })
                        }
                      >
                        <option value="false">Block / revoke</option>
                        <option value="true">Allow with evidence</option>
                      </select>
                    </label>
                    <label className="field">
                      <span>Purpose</span>
                      <select
                        value={permission.purpose}
                        onChange={(e) => setPermission({ ...permission, purpose: e.target.value })}
                      >
                        {(permission.channel === 'ads'
                          ? [
                              'advertising',
                              'advertising_measurement',
                              'advertising+advertising_measurement',
                            ]
                          : ['direct_sales', 'marketing', 'service_update']
                        ).map((s) => (
                          <option key={s} value={s}>
                            {s === 'advertising+advertising_measurement'
                              ? 'Advertising audience + measurement and delivery optimization'
                              : s === 'advertising_measurement'
                                ? 'Ad conversion measurement'
                                : s === 'advertising'
                                  ? 'Advertising audience'
                                  : s.replaceAll('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Evidence source</span>
                      <input
                        required
                        minLength={3}
                        value={permission.source}
                        onChange={(e) => setPermission({ ...permission, source: e.target.value })}
                        placeholder="Consent form or verified interaction"
                      />
                    </label>
                    <label className="field field-full">
                      <span>Evidence reference</span>
                      <input
                        required
                        minLength={5}
                        value={permission.evidence}
                        onChange={(e) => setPermission({ ...permission, evidence: e.target.value })}
                        placeholder="Form receipt, interaction ID, or recorded request"
                      />
                    </label>
                    <label className="field field-full">
                      <span>Reason</span>
                      <textarea
                        required
                        minLength={5}
                        rows={2}
                        value={permission.reason}
                        onChange={(e) => setPermission({ ...permission, reason: e.target.value })}
                      />
                    </label>
                  </div>
                  <p className="form-note">
                    This decision needs a permitted governance role. Granting permission never
                    overrides global suppression or source eligibility.
                  </p>
                  <div className="modal-footer">
                    <button type="button" className="btn" onClick={() => setPermissionOpen(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" disabled={busy}>
                      Save evidence & permission
                    </button>
                  </div>
                </form>
              )}
              <section className="drawer-section">
                <SectionHeading title="Source & provenance" />
                {c.provenance?.map((p: any, i: number) => (
                  <div className="provenance-row" key={i}>
                    <Link2 size={15} />
                    <div>
                      <strong>{p.source}</strong>
                      <small>
                        {p.sourceClass?.replaceAll('_', ' ')} · Collected {dateLabel(p.collectedAt)}
                      </small>
                    </div>
                    <Badge>{i === 0 ? 'Original source' : 'Linked source'}</Badge>
                  </div>
                ))}
              </section>
              <section className="suppression-panel">
                <div>
                  <LockKeyhole size={18} />
                  <h3>{c.suppressed ? 'Global suppression is active' : 'Outreach controls'}</h3>
                </div>
                <p>
                  {c.suppressed
                    ? 'All outreach is blocked. Lifting suppression will preserve each existing channel permission.'
                    : 'Pause all outreach to this relationship immediately. The reason is recorded in the audit log.'}
                </p>
                <label className="field">
                  <span>Reason for this change</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Record the request or supporting evidence"
                  />
                </label>
                <button
                  className={`btn ${c.suppressed ? 'btn-primary' : 'btn-danger'}`}
                  disabled={busy || !canSuppress || reason.trim().length < 3}
                  onClick={suppression}
                >
                  {c.suppressed ? 'Lift global suppression' : 'Suppress all outreach'}
                </button>
              </section>
              <section className="drawer-section">
                <SectionHeading
                  title="Resolve a duplicate"
                  subtitle="Merge only after confirming identity. Permissions use the more restrictive choice."
                />
                <div className="form-grid">
                  <label className="field field-full">
                    <span>Duplicate relationship</span>
                    <select value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
                      <option value="">Select a verified duplicate</option>
                      {data.contacts
                        .filter((x) => x.id !== c.id)
                        .map((x) => (
                          <option value={x.id} key={x.id}>
                            {x.name} · {x.email}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="field field-full">
                    <span>Identity verification reason</span>
                    <input
                      value={mergeReason}
                      onChange={(e) => setMergeReason(e.target.value)}
                      placeholder="e.g. Advisor confirmed the same verified phone"
                    />
                  </label>
                </div>
                <button
                  className="btn"
                  disabled={!canSuppress || !mergeId || mergeReason.length < 5}
                  onClick={async () => {
                    try {
                      const r = await mutate(`/contacts/${c.id}/merge`, 'POST', {
                        duplicateId: mergeId,
                        reason: mergeReason,
                      });
                      setLastMerge(r.mergeId);
                      setMergeId('');
                      notify('Duplicate merged with audit history');
                    } catch {}
                  }}
                >
                  Merge with this relationship
                </button>
                {lastMerge && (
                  <button
                    className="btn btn-ghost"
                    onClick={async () => {
                      try {
                        await mutate(`/merges/${lastMerge}/undo`, 'POST', {});
                        setLastMerge('');
                        notify('Merge undone');
                      } catch {}
                    }}
                  >
                    Undo last merge
                  </button>
                )}
              </section>
            </>
          )}
        </div>
        <div className="drawer-footer">
          <span>
            <ShieldCheck size={12} />
            Changes are saved with an audit trail
          </span>
          <span>v{c.version}</span>
        </div>
      </motion.aside>
    </motion.div>
  );
}
function UsersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="4" r="2.5" stroke="currentColor" />
      <path d="M2 12c0-3 10-3 10 0" stroke="currentColor" />
    </svg>
  );
}
function TargetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" />
      <circle cx="9" cy="9" r="3" stroke="currentColor" />
    </svg>
  );
}
